import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import type { Editor } from '@tiptap/react';

import type { Suggestion } from '@/state';
import type { PersistedDocumentWriter } from './use-llm-agent';
import type { PendingReview } from './use-llm-agent/review/persisted-review/persisted-review';

export type SuggestionDiffConfig = Parameters<ReturnType<typeof getAiToolkit>['setSuggestionsFromDiff']>[0];

export type EditorDocumentInfo = {
  documentId?: string;
  editorType?: string;
  agentActive?: boolean;
  isComponentMounted?: boolean;
  persistDocumentPatch?: PersistedDocumentWriter;
  hydratedReview?: PendingReview;
  hydrationGeneration?: number;
  terminalResolutionStarted?: boolean;
};

type EditorWithDocumentInfo = Pick<Editor, 'storage'>;
type EditorWithSuggestionSelection = Pick<Editor, 'commands' | 'storage'>;

const getDocumentInfo = (editor?: EditorWithDocumentInfo | null): EditorDocumentInfo | null =>
  editor ? editor.storage.documentInfo : null;

export const isEditorComponentMounted = (editor?: EditorWithDocumentInfo | null): boolean =>
  !!getDocumentInfo(editor)?.isComponentMounted;

export const setEditorDocumentId = (editor?: EditorWithDocumentInfo | null, documentId?: string): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.documentId = documentId;
  }
};

export const setEditorType = (editor?: EditorWithDocumentInfo | null, editorType?: string): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.editorType = editorType;
  }
};

export const setEditorAgentActive = (editor: EditorWithDocumentInfo | null | undefined, agentActive: boolean): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.agentActive = agentActive;
  }
};

export const setEditorComponentMounted = (
  editor: EditorWithDocumentInfo | null | undefined,
  isComponentMounted: boolean
): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.isComponentMounted = isComponentMounted;
  }
};

export const setEditorPersistDocumentPatch = (
  editor: EditorWithDocumentInfo | null | undefined,
  persistDocumentPatch?: PersistedDocumentWriter
): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.persistDocumentPatch = persistDocumentPatch;
  }
};

export const getEditorHydratedReview = (editor?: EditorWithDocumentInfo | null): PendingReview | undefined =>
  getDocumentInfo(editor)?.hydratedReview;

export const setEditorHydratedReview = (
  editor: EditorWithDocumentInfo | null | undefined,
  hydratedReview?: PendingReview
): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.hydratedReview = hydratedReview;
  }
};

export const getEditorHydrationGeneration = (editor?: EditorWithDocumentInfo | null): number =>
  getDocumentInfo(editor)?.hydrationGeneration ?? 0;

export const bumpEditorHydrationGeneration = (editor: EditorWithDocumentInfo | null | undefined): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.hydrationGeneration = (documentInfo.hydrationGeneration ?? 0) + 1;
  }
};

export const hasTerminalResolutionStarted = (editor?: EditorWithDocumentInfo | null): boolean =>
  !!getDocumentInfo(editor)?.terminalResolutionStarted;

export const setTerminalResolutionStarted = (
  editor: EditorWithDocumentInfo | null | undefined,
  terminalResolutionStarted: boolean
): void => {
  const documentInfo = getDocumentInfo(editor);
  if (documentInfo) {
    documentInfo.terminalResolutionStarted = terminalResolutionStarted;
  }
};

export const syncSuggestionsFromDiff = (editor: Editor, config: SuggestionDiffConfig): Suggestion[] => {
  const toolkit = getAiToolkit(editor);
  toolkit.setSuggestionsFromDiff(config);
  return toolkit.getSuggestions() as Suggestion[];
};

export const setSelectedSuggestion = (
  editor: EditorWithSuggestionSelection | null | undefined,
  suggestionId?: string | null
): boolean => {
  if (!editor || !suggestionId) return false;

  const pluginKey = (editor.storage as any)?.aiToolkit?.pluginKey;
  if (!pluginKey || typeof editor.commands?.command !== 'function') return false;

  return editor.commands.command(({ tr, dispatch }) => {
    tr.setMeta(pluginKey, {
      type: 'setSelectedSuggestion',
      payload: { suggestionId },
    });
    dispatch?.(tr);
    return true;
  });
};
